import React, { useEffect } from 'react';
import { Button, Dropdown, Spin, Table, TableColumnsType, TableProps } from 'antd';
import { EllipsisOutlined,
  ExclamationCircleOutlined,
  LoadingOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useLayoutContext } from '../../Layout/context';
import { useSearchParams } from '../../../hooks/useSearchParams';
import { useSimpleModal } from '../../Common/Modal/Simple';
import displayDate from '../../../utils/date';
import { SEOOrderColumn, SEOResponse, useSEODelete, useSEOGet } from '../../../hooks/api/SEO';
import { getValidSearchParams } from '../../../utils';
import ContentHeader from '../../Common/ContentHeader';
import { useMessageError } from '../../../hooks/common';
import ButtonEye from './ButtonEye';

import styles from './index.module.scss';

export default function Pages(): JSX.Element {
  const { setBreadcrumbs } = useLayoutContext();
  const navigate = useNavigate();
  const seoGet = useSEOGet();
  const seoDelete = useSEODelete();
  const { searchParams, replaceSearchParams } = useSearchParams();
  const { modal, contextHolder } = useSimpleModal();
  const fetch = () => {
    seoGet.fetch({
      page: 1,
      pageSize: 10,
      ...getValidSearchParams(
        (keys) => keys.filter((key) => key.indexOf('comment_') !== 0),
        searchParams,
      ),
    });
  };

  useMessageError([seoGet, seoDelete]);

  useEffect(() => {
    setBreadcrumbs([{ title: 'Pages' }]);
  }, []);

  useEffect(fetch, [searchParams, seoDelete.data]);

  const onChange: TableProps<SEOResponse>['onChange'] = (
    pagination,
    _,
    sorter,
  ) => {
    replaceSearchParams({
      page: pagination.current || 1,
      pageSize: pagination.pageSize || 10,
      orderByColumn: (Array.isArray(sorter) ? sorter[0].field : sorter.field) as SEOOrderColumn,
      // eslint-disable-next-line no-nested-ternary
      orderBy: (Array.isArray(sorter) ? sorter[0].order : sorter.order)
        ? ((Array.isArray(sorter) ? sorter[0].order : sorter.order) === 'ascend'
          ? 'ASC'
          : 'DESC')
        : '',
    });
  };

  const confirmDelete = (seoId: string) => {
    if (seoId) {
      modal.confirm({
        title: 'Are you sure you want to Delete the page?',
        icon: <ExclamationCircleOutlined />,
        onOk: () => seoDelete.fetch(seoId),
        okText: 'Delete',
      });
    }
  };

  const confirmDeactivate = (seoId: string) => {
    if (seoId) {
      modal.confirm({
        title: 'Are you sure you want to show the page?',
        icon: <ExclamationCircleOutlined />,
        onOk: () => {}, // seoUpdate({ id: seoId, body: { status: TicketStatus.RESOLVED } }),
        okText: 'Set active',
      });
    }
  };

  const confirmActivate = (seoId: string) => {
    if (seoId) {
      modal.confirm({
        title: 'Are you sure you want to hide the page?',
        icon: <ExclamationCircleOutlined />,
        onOk: () => {}, // seoUpdate({ id: seoId, body: { status: TicketStatus.OPEN } }),
        okText: 'Deactivate',
      });
    }
  };

  const columns: TableColumnsType<SEOResponse> = [
    {
      title: 'Page/Key',
      dataIndex: 'key',
      sorter: true,
    },
    {
      title: 'Header',
      dataIndex: 'name',
      sorter: true,
    },
    {
      title: 'SEO title',
      dataIndex: 'title',
      sorter: true,
    },
    {
      title: 'Date modified',
      dataIndex: 'updatedAt',
      width: 190,
      sorter: true,
      render: (updatedAt) => displayDate(updatedAt),
    },
    {
      title: 'Actions',
      width: 50,
      dataIndex: 'actions',
      render: (_, seo) => (
        <div className={styles.actions}>
          <ButtonEye seo={seo} onChange={fetch} loading={seoGet.loading} />

          <Dropdown
            menu={{
              items: [
                {
                  key: '1',
                  label: 'Edit',
                  onClick: () => {
                    navigate(`/pages/${seo.id}`);
                  },
                },
                {
                  key: '2',
                  label: 'Delete',
                  onClick: () => confirmDelete(seo.id),
                },
              ],
            }}
            trigger={['click']}
          >
            <Button icon={<EllipsisOutlined />} />
          </Dropdown>
        </div>
      ),
    },
  ];

  return (
    <>
      <ContentHeader title="Pages list">
        <Button
          type="primary"
          onClick={(e) => {
            e.preventDefault();
            navigate('create');
          }}
        >
          Create
        </Button>
      </ContentHeader>
      {contextHolder}

      <Table<SEOResponse>
        columns={columns}
        rowKey="id"
        style={{ minHeight: 'calc(100vh - 128px)' }}
        dataSource={seoGet.data?.data || []}
        onChange={onChange}
        pagination={{
          current: +(searchParams.get('page') || 1) || 1,
          pageSize: +(searchParams.get('pageSize') || 10) || 10,
          total: seoGet.data?.meta.itemCount || 0,
          showQuickJumper: true,
          showSizeChanger: true,
          showTotal: (total) => `Total ${total} item${total > 1 ? 's' : ''}`,
        }}
      />

      {seoGet.loading ? (
        <div className="bgSpin">
          <Spin indicator={<LoadingOutlined spin />} size="large" />
        </div>
      ) : null}
    </>
  );
}
